import { useEffect, useState, useRef } from 'react';
import { SITE_CONFIG } from '../../../config/constants';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef<HTMLElement>(null);
  const [cardVisible, setCardVisible] = useState(false);
  const [rightVisible, setRightVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsVisible(true);
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const createObs = (
      setter: (v: boolean) => void,
      opts?: { threshold?: number; rootMargin?: string }
    ) => {
      return new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setter(true);
        },
        { threshold: 0.12, rootMargin: '0px 0px -50px 0px', ...opts }
      );
    };
    const o1 = createObs(setCardVisible, { threshold: 0.08 });
    const o2 = createObs(setRightVisible, { threshold: 0.08 });
    const o3 = createObs(setStatsVisible, { threshold: 0.15 });

    if (cardRef.current) o1.observe(cardRef.current);
    if (rightRef.current) o2.observe(rightRef.current);
    if (statsRef.current) o3.observe(statsRef.current);

    return () => {
      o1.disconnect();
      o2.disconnect();
      o3.disconnect();
    };
  }, []);

  const infoGridItems = [
    {
      icon: 'ri-shield-check-line',
      label: 'Ownership',
      value: 'Verified & Secured',
    },
    { icon: 'ri-global-line', label: 'Extension', value: '.ORG (Trusted)' },
    { icon: 'ri-exchange-dollar-line', label: 'Transfer', value: 'Immediate' },
    { icon: 'ri-vip-crown-line', label: 'Status', value: 'Premium Asset' },
  ];

  const valuePoints = [
    {
      icon: 'ri-award-line',
      title: 'Authority & Trust',
      desc: 'The .ORG extension carries inherent credibility recognized globally',
      num: '01',
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Power Keywords',
      desc: '"International" + "Intelligence" — commanding presence in any sector',
      num: '02',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Investment Grade',
      desc: 'Premium domains appreciate over time with consistent value growth',
      num: '03',
    },
    {
      icon: 'ri-rocket-2-line',
      title: 'Instant Transfer',
      desc: 'Secure, verified ownership with immediate transfer capability',
      num: '04',
    },
  ];

  const statsItems = [
    { value: '.ORG', label: 'Trusted TLD', icon: 'ri-global-line' },
    { value: '2', label: 'Power Keywords', icon: 'ri-key-2-line' },
    { value: '6+', label: 'Industry Fit', icon: 'ri-building-line' },
    { value: '\u221E', label: 'Potential', icon: 'ri-infinity-line' },
    {
      value: 'Ready',
      label: 'For Transfer',
      isGreen: true,
      icon: 'ri-exchange-line',
    },
  ];

  return (
    <section
      ref={heroRef}
      id="home"
      className="relative min-h-screen overflow-hidden bg-[#050505]"
    >
      {/* ═══ LAYERED BACKGROUND ═══ */}
      <div className="absolute inset-0">
        {/* Premium gradient background - replaces external image dependency */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 50% 20%, rgba(217,169,78,0.15) 0%, transparent 50%),
              radial-gradient(ellipse 60% 40% at 80% 60%, rgba(217,169,78,0.08) 0%, transparent 50%),
              radial-gradient(ellipse 50% 30% at 20% 70%, rgba(217,169,78,0.06) 0%, transparent 50%),
              linear-gradient(135deg, rgba(217,169,78,0.03) 0%, transparent 25%, transparent 75%, rgba(217,169,78,0.02) 100%),
              #050505
            `,
          }}
        ></div>
        {/* Animated golden wave effect */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: `
              repeating-linear-gradient(
                45deg,
                transparent,
                transparent 100px,
                rgba(217,169,78,0.02) 100px,
                rgba(217,169,78,0.02) 200px
              )
            `,
            animation: 'shimmer 20s linear infinite',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-[#050505]"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50"></div>
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)',
          }}
        ></div>
      </div>

      {/* Animated light beams */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-1/4 w-px h-full opacity-[0.04]"
          style={{
            background:
              'linear-gradient(to bottom, transparent, rgba(217,169,78,0.8) 30%, rgba(217,169,78,0.8) 70%, transparent)',
            animation: 'beamPulse 6s ease-in-out infinite',
          }}
        ></div>
        <div
          className="absolute top-0 right-1/4 w-px h-full opacity-[0.03]"
          style={{
            background:
              'linear-gradient(to bottom, transparent, rgba(217,169,78,0.6) 40%, rgba(217,169,78,0.6) 60%, transparent)',
            animation: 'beamPulse 6s ease-in-out 2s infinite',
          }}
        ></div>
        <div
          className="absolute top-0 left-1/2 w-px h-full opacity-[0.02]"
          style={{
            background:
              'linear-gradient(to bottom, transparent, rgba(217,169,78,0.5) 20%, rgba(217,169,78,0.5) 80%, transparent)',
            animation: 'beamPulse 6s ease-in-out 4s infinite',
          }}
        ></div>
      </div>

      {/* Animated radial glow following mouse */}
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(217,169,78,0.07) 0%, rgba(217,169,78,0.02) 35%, transparent 70%)',
          transform: `translate(calc(-50% + ${mousePosition.x * 0.5}px), calc(-50% + ${mousePosition.y *
            0.5}px))`,
          transition:
            'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)',
        }}
      ></div>

      {/* Secondary ambient orb */}
      <div
        className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full opacity-30 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(217,169,78,0.06) 0%, transparent 60%)',
          transform: `translate(${-mousePosition.x * 0.3}px, ${-mousePosition.y *
            0.3}px)`,
          transition:
            'transform 0.8s cubic-bezier(0.23, 1, 0.32, 1)',
        }}
      ></div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(35)].map((_, i) => {
          const size = Math.random() * 3 + 1;
          const left = Math.random() * 100;
          const top = Math.random() * 100;
          const delay = Math.random() * 5;
          const duration = 3 + Math.random() * 4;
          return (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                background: `rgba(217, 169, 78, ${Math.random() *
                  0.35 +
                  0.1})`,
                left: `${left}%`,
                top: `${top}%`,
                animation: `floatParticle ${duration}s ease-in-out ${delay}s infinite alternate`,
                boxShadow:
                  size > 2
                    ? '0 0 8px rgba(217,169,78,0.3)'
                    : 'none',
              }}
            ></div>
          );
        })}
      </div>

      {/* Fine grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.008]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(217,169,78,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(217,169,78,0.5) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      ></div>

      {/* Monogram watermark */}
      <div className="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 opacity-[0.012] pointer-events-none select-none">
        <span className="text-[350px] md:text-[450px] font-bold text-white whitespace-nowrap leading-none font-display">
          II
        </span>
      </div>

      {/* ═══ DECORATIVE FRAMES ═══ */}
      <div className="absolute top-24 left-8 w-36 h-36 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-amber-500/30 to-transparent"></div>
        <div className="absolute top-0 left-0 h-full w-px bg-gradient-to-b from-amber-500/30 to-transparent"></div>
        <div
          className="absolute top-0 left-0 w-2 h-2 bg-amber-500/40 rounded-full"
          style={{ boxShadow: '0 0 8px rgba(217,169,78,0.3)' }}
        ></div>
        <div className="absolute top-6 left-6 w-1 h-1 bg-amber-500/20 rounded-full"></div>
      </div>
      <div className="absolute top-24 right-8 w-36 h-36 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-px bg-gradient-to-l from-amber-500/30 to-transparent"></div>
        <div className="absolute top-0 right-0 h-full w-px bg-gradient-to-b from-amber-500/30 to-transparent"></div>
        <div
          className="absolute top-0 right-0 w-2 h-2 bg-amber-500/40 rounded-full"
          style={{ boxShadow: '0 0 8px rgba(217,169,78,0.3)' }}
        ></div>
        <div className="absolute top-6 right-6 w-1 h-1 bg-amber-500/20 rounded-full"></div>
      </div>
      <div className="absolute bottom-12 left-8 w-36 h-36 pointer-events-none">
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-amber-500/15 to-transparent"></div>
        <div className="absolute bottom-0 left-0 h-full w-px bg-gradient-to-t from-amber-500/15 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-1.5 h-1.5 bg-amber-500/30 rounded-full"></div>
      </div>
      <div className="absolute bottom-12 right-8 w-36 h-36 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-full h-px bg-gradient-to-l from-amber-500/15 to-transparent"></div>
        <div className="absolute bottom-0 right-0 h-full w-px bg-gradient-to-t from-amber-500/15 to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-1.5 h-1.5 bg-amber-500/30 rounded-full"></div>
      </div>

      {/* Vertical side accent lines */}
      <div className="absolute top-1/3 left-12 w-px h-48 bg-gradient-to-b from-transparent via-amber-500/10 to-transparent pointer-events-none hidden lg:block"></div>
      <div className="absolute top-1/3 right-12 w-px h-48 bg-gradient-to-b from-transparent via-amber-500/10 to-transparent pointer-events-none hidden lg:block"></div>
      <div className="absolute top-[30%] left-0 w-20 h-px bg-gradient-to-r from-amber-500/10 to-transparent pointer-events-none hidden lg:block"></div>
      <div className="absolute top-[30%] right-0 w-20 h-px bg-gradient-to-l from-amber-500/10 to-transparent pointer-events-none hidden lg:block"></div>

      {/* ═══ TOP STATUS BADGES ═══ */}
      <div
        className={`absolute top-28 left-8 md:left-12 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
        }`}
      >
        <div className="relative group">
          <div className="absolute -inset-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <div className="absolute top-0 left-0 w-3 h-3 border-l border-t border-emerald-400/20 rounded-tl-lg"></div>
            <div className="absolute top-0 right-0 w-3 h-3 border-r border-t border-emerald-400/20 rounded-tr-lg"></div>
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l border-b border-emerald-400/20 rounded-bl-lg"></div>
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r border-b border-emerald-400/20 rounded-br-lg"></div>
          </div>
          <div className="relative flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emerald-500/[0.08] via-emerald-400/[0.06] to-emerald-500/[0.04] backdrop-blur-xl border border-emerald-400/20 rounded-full overflow-hidden shadow-lg shadow-emerald-500/10">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/10 via-transparent to-transparent"></div>
            <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-emerald-300/40 to-transparent"></div>
            <div className="absolute bottom-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-emerald-400/20 to-transparent"></div>
            <div className="relative w-2.5 h-2.5 flex items-center justify-center">
              <div className="absolute inset-0 bg-emerald-400 rounded-full shadow-[0_0_12px_rgba(52,211,153,0.6)]"></div>
              <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-50"></div>
              <div className="absolute inset-0.5 bg-emerald-300 rounded-full"></div>
            </div>
            <span className="relative text-[10px] tracking-[0.2em] text-emerald-300 uppercase font-medium font-mono">
              Available Now
            </span>
            <div className="relative w-1 h-1 bg-emerald-400/40 rounded-full"></div>
          </div>
        </div>
      </div>

      <div
        className={`absolute top-28 right-8 md:right-12 transition-all duration-1000 delay-700 ${
          isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
        }`}
      >
        <div className="relative group">
          <div className="absolute -inset-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <div className="absolute top-0 left-0 w-3 h-3 border-l border-t border-amber-400/20 rounded-tl-lg"></div>
            <div className="absolute top-0 right-0 w-3 h-3 border-r border-t border-amber-400/20 rounded-tr-lg"></div>
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l border-b border-amber-400/20 rounded-bl-lg"></div>
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r border-b border-amber-400/20 rounded-br-lg"></div>
          </div>
          <div className="relative flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-500/[0.12] via-amber-400/[0.08] to-amber-500/[0.06] backdrop-blur-xl border border-amber-400/30 rounded-full overflow-hidden shadow-lg shadow-amber-500/10">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 via-transparent to-transparent"></div>
            <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-300/50 to-transparent"></div>
            <div className="absolute bottom-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-amber-400/30 to-transparent"></div>
            <div className="relative">
              <div className="absolute inset-0 blur-md bg-amber-400/30"></div>
              <i className="relative ri-vip-crown-2-line text-amber-300 text-base drop-shadow-[0_0_8px_rgba(252,211,77,0.5)]"></i>
            </div>
            <span className="relative text-[10px] tracking-[0.2em] text-amber-300 uppercase font-medium font-mono">
              Premium Asset
            </span>
            <div className="relative w-1 h-1 bg-amber-400/40 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* ═══ MAIN CONTENT ═══ */}
      <div className="relative z-10 pt-44 pb-24 px-6">
        <div className="max-w-7xl mx-auto w-full">
          {/* ── TOP BADGE ── */}
          <div
            className={`flex justify-center mb-10 transition-all duration-1000 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-6'
            }`}
          >
            <div className="relative inline-flex items-center gap-4">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500/50"></div>
              <div className="relative group">
                <div className="absolute -inset-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber-400/30 rounded-tl-xl"></div>
                  <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber-400/30 rounded-tr-xl"></div>
                  <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber-400/30 rounded-bl-xl"></div>
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber-400/30 rounded-br-xl"></div>
                </div>
                <div
                  className="relative flex items-center gap-3 px-8 py-3 rounded-full overflow-hidden"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(217,169,78,0.12) 0%, rgba(217,169,78,0.06) 50%, rgba(217,169,78,0.08) 100%)',
                    border: '1px solid rgba(217,169,78,0.25)',
                    boxShadow:
                      '0 8px 32px rgba(217,169,78,0.15), inset 0 1px 0 rgba(255,255,255,0.1)',
                  }}
                >
                  <div className="absolute top-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-amber-300/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-1/3 right-1/3 h-px bg-gradient-to-r from-transparent via-amber-400/20 to-transparent"></div>
                  <div className="absolute inset-0 bg-gradient-radial from-amber-400/5 via-transparent to-transparent"></div>
                  <div className="relative flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-amber-400 rounded-full shadow-[0_0_8px_rgba(217,169,78,0.6)]"></div>
                    <div className="w-1 h-1 bg-amber-400/60 rounded-full"></div>
                  </div>
                  <span className="text-[10px] font-medium tracking-[0.35em] text-amber-400 uppercase drop-shadow-[0_0_12px_rgba(217,169,78,0.4)] font-mono">
                    Exclusive Domain Offering
                  </span>
                  <div className="relative flex items-center gap-1.5">
                    <div className="w-1 h-1 bg-amber-400/60 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-amber-400 rounded-full shadow-[0_0_8px_rgba(217,169,78,0.6)]"></div>
                  </div>
                </div>
              </div>
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500/50"></div>
            </div>
          </div>

          {/* ── DOMAIN NAME DISPLAY ── */}
          <div
            className={`text-center mb-6 transition-all duration-1200 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h1 className="relative">
              <span className="block overflow-hidden mb-3">
                <span className="inline-block text-5xl md:text-7xl lg:text-[5.5rem] font-light text-white tracking-[0.08em] leading-none font-display"
                  style={{
                    animation: isVisible
                      ? 'slideUpReveal 1s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both'
                      : 'none',
                    textShadow: '0 0 80px rgba(217,169,78,0.1)',
                  }}
                >
                  INTERNATIONAL
                </span>
              </span>
              <span className="flex items-center justify-center gap-4 md:gap-8">
                <span className="hidden md:flex items-center gap-2 flex-1 max-w-[100px]">
                  <span className="flex-1 h-px bg-gradient-to-r from-transparent to-amber-500/30"></span>
                  <span className="w-1.5 h-1.5 rotate-45 border border-amber-500/40 bg-amber-500/10"></span>
                </span>
                <span
                  className="inline-block text-4xl md:text-6xl lg:text-[4.5rem] font-light tracking-[0.12em] leading-none font-display"
                  style={{
                    background:
                      'linear-gradient(135deg, #f5d78e 0%, #d4a843 20%, #f7e8b5 45%, #c9952e 70%, #f5d78e 100%)',
                    backgroundSize: '200% 200%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: isVisible
                      ? 'slideUpReveal 1s cubic-bezier(0.16, 1, 0.3, 1) 0.5s both, goldShimmer 4s ease-in-out infinite'
                      : 'none',
                    filter: 'drop-shadow(0 0 30px rgba(217,169,78,0.15))',
                  }}
                >
                  INTELLIGENCE
                </span>
                <span className="hidden md:flex items-center gap-2 flex-1 max-w-[100px]">
                  <span className="w-1.5 h-1.5 rotate-45 border border-amber-500/40 bg-amber-500/10"></span>
                  <span className="flex-1 h-px bg-gradient-to-l from-transparent to-amber-500/30"></span>
                </span>
              </span>
              <span className="block mt-4 overflow-hidden">
                <span className="inline-block text-2xl md:text-3xl lg:text-4xl font-extralight text-white/25 tracking-[0.35em] font-display"
                  style={{
                    animation: isVisible
                      ? 'slideUpReveal 1s cubic-bezier(0.16, 1, 0.3, 1) 0.7s both'
                      : 'none',
                  }}
                >
                  .ORG
                </span>
              </span>
            </h1>
          </div>

          {/* ── Tagline ── */}
          <div
            className={`text-center mb-10 transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="text-lg md:text-xl text-white/35 font-light tracking-wide max-w-xl mx-auto mb-3 font-display">
              A Rare Digital Asset of Exceptional Value
            </p>
            <p className="text-[11px] text-white/25 tracking-[0.15em] max-w-md mx-auto uppercase font-mono">
              Commanding presence in intelligence, consulting &
              strategic sectors
            </p>
          </div>

          {/* ── Ornamental Divider ── */}
          <div className="flex items-center justify-center gap-3 mb-16">
            <div className="h-px w-28 bg-gradient-to-r from-transparent via-amber-500/15 to-amber-500/35"></div>
            <div className="flex items-center gap-2">
              <div className="w-1 h-1 bg-amber-500/30 rounded-full"></div>
              <div className="relative w-3 h-3 rotate-45 border border-amber-500/40 bg-amber-500/10">
                <div className="absolute inset-0.5 border border-amber-500/20 rotate-0"></div>
              </div>
              <div className="w-1 h-1 bg-amber-500/30 rounded-full"></div>
            </div>
            <div className="h-px w-28 bg-gradient-to-l from-transparent via-amber-500/15 to-amber-500/35"></div>
          </div>

          {/* ═══ TWO-COLUMN LAYOUT ═══ */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-20">
            {/* LEFT — Premium Domain Card */}
            <div
              ref={cardRef}
              className={`transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                cardVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
              }`}
            >
              <div className="relative">
                {/* Outer decorative frames */}
                <div
                  className="absolute -inset-3 rounded-2xl"
                  style={{ border: '1px solid rgba(217,169,78,0.08)' }}
                ></div>
                <div
                  className="absolute -inset-6 rounded-3xl"
                  style={{ border: '1px solid rgba(217,169,78,0.04)' }}
                ></div>
                <div className="absolute -top-6 -left-6 w-2 h-2 rotate-45 bg-amber-500/20 border border-amber-500/30"></div>
                <div className="absolute -top-6 -right-6 w-2 h-2 rotate-45 bg-amber-500/20 border border-amber-500/30"></div>
                <div className="absolute -bottom-6 -left-6 w-2 h-2 rotate-45 bg-amber-500/15 border border-amber-500/20"></div>
                <div className="absolute -bottom-6 -right-6 w-2 h-2 rotate-45 bg-amber-500/15 border border-amber-500/20"></div>

                {/* Main Card */}
                <div
                  className="relative rounded-xl overflow-hidden"
                  style={{
                    background:
                      'linear-gradient(145deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 50%, rgba(217,169,78,0.03) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    boxShadow:
                      '0 25px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(217,169,78,0.05), inset 0 1px 0 rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-40 bg-gradient-to-b from-amber-500/[0.04] to-transparent pointer-events-none"></div>
                  <div
                    className="absolute inset-0 opacity-[0.025]"
                    style={{
                      backgroundImage:
                        'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                      backgroundSize: '20px 20px',
                    }}
                  ></div>
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent"></div>

                  <div className="relative p-8 md:p-9">
                    {/* Card Header */}
                    <div
                      className="flex items-center justify-between mb-7"
                      style={{
                        opacity: cardVisible ? 1 : 0,
                        transform: cardVisible
                          ? 'translateY(0)'
                          : 'translateY(12px)',
                        transition:
                          'opacity 700ms cubic-bezier(0.22,1,0.36,1) 150ms, transform 700ms cubic-bezier(0.22,1,0.36,1) 150ms',
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div
                            className="w-10 h-10 flex items-center justify-center rounded-lg overflow-hidden"
                            style={{
                              background:
                                'linear-gradient(135deg, rgba(217,169,78,0.2) 0%, rgba(217,169,78,0.08) 100%)',
                              border: '1px solid rgba(217,169,78,0.3)',
                              boxShadow:
                                '0 0 15px rgba(217,169,78,0.1)',
                            }}
                          >
                            <i className="ri-global-line text-amber-400 text-lg"></i>
                          </div>
                          <div className="absolute -inset-1 rounded-xl border border-amber-500/10"></div>
                        </div>
                        <div>
                          <span className="block text-[10px] tracking-[0.2em] text-white/50 uppercase font-mini-title font-medium">
                            Premium Domain
                          </span>
                          <span className="block text-[9px] tracking-[0.15em] text-amber-500/40 uppercase mt-0.5 font-mini-title font-normal">
                            Exclusive Listing
                          </span>
                        </div>
                      </div>
                      <div className="relative">
                        <span
                          className="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[9px] tracking-wider uppercase font-normal font-mono"
                          style={{
                            background:
                              'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(16,185,129,0.05) 100%)',
                            border: '1px solid rgba(16,185,129,0.25)',
                            color: 'rgba(52,211,153,0.9)',
                          }}
                        >
                          <span className="relative w-1.5 h-1.5">
                            <span className="absolute inset-0 bg-emerald-400 rounded-full"></span>
                            <span className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-40"></span>
                          </span>
                          For Sale
                        </span>
                      </div>
                    </div>

                    <div
                      className="h-px w-full bg-gradient-to-r from-transparent via-white/[0.06] to-transparent mb-7"
                      style={{
                        opacity: cardVisible ? 1 : 0,
                        transform: cardVisible
                          ? 'scaleX(1)'
                          : 'scaleX(0)',
                        transition:
                          'opacity 600ms cubic-bezier(0.22,1,0.36,1) 250ms, transform 600ms cubic-bezier(0.22,1,0.36,1) 250ms',
                      }}
                    ></div>

                    {/* Domain Name */}
                    <div
                      className="mb-7"
                      style={{
                        opacity: cardVisible ? 1 : 0,
                        transform: cardVisible
                          ? 'translateY(0)'
                          : 'translateY(10px)',
                        transition:
                          'opacity 700ms cubic-bezier(0.22,1,0.36,1) 300ms, transform 700ms cubic-bezier(0.22,1,0.36,1) 300ms',
                      }}
                    >
                      <div className="text-xl md:text-2xl font-light text-white mb-2 tracking-wide font-display">
                        {SITE_CONFIG.domain.name.replace('.org', '')}
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className="text-lg font-light font-display"
                          style={{
                            background:
                              'linear-gradient(135deg, #f5d78e, #c9952e)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          {SITE_CONFIG.domain.tld}
                        </span>
                        <div className="w-px h-3 bg-white/10"></div>
                        <span className="text-[10px] text-white/35 tracking-wider uppercase font-mono">
                          Trusted TLD
                        </span>
                        <div className="w-px h-3 bg-white/10"></div>
                        <span className="text-[10px] text-white/35 tracking-wider uppercase font-mono">
                          26 Characters
                        </span>
                      </div>
                    </div>

                    {/* Info Grid — each item staggers */}
                    <div className="grid grid-cols-2 gap-3 mb-7">
                      {infoGridItems.map((item, i) => (
                        <div
                          key={i}
                          className="group relative p-3.5 rounded-lg overflow-hidden cursor-default"
                          style={{
                            background:
                              'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                            border: '1px solid rgba(255,255,255,0.05)',
                            opacity: cardVisible ? 1 : 0,
                            transform: cardVisible
                              ? 'translateY(0)'
                              : 'translateY(14px)',
                            transition: `opacity 650ms cubic-bezier(0.22,1,0.36,1) ${400 +
                              i * 80}ms, transform 650ms cubic-bezier(0.22,1,0.36,1) ${400 +
                              i * 80}ms, border-color 500ms, background 500ms`,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor =
                              'rgba(217,169,78,0.2)';
                            e.currentTarget.style.background =
                              'linear-gradient(135deg, rgba(217,169,78,0.05) 0%, rgba(255,255,255,0.02) 100%)';
                            e.currentTarget.style.boxShadow =
                              '0 8px 30px rgba(0,0,0,0.2), 0 0 0 1px rgba(217,169,78,0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor =
                              'rgba(255,255,255,0.05)';
                            e.currentTarget.style.background =
                              'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i
                                className={`${item.icon} text-amber-500/60 text-xs`}
                              ></i>
                            </div>
                            <span className="text-[9px] tracking-[0.15em] text-white/35 uppercase font-mono">
                              {item.label}
                            </span>
                          </div>
                          <div className="text-xs text-white/75 font-medium pl-7 font-body">
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Owner Footer */}
                    <div
                      className="pt-6"
                      style={{
                        borderTop: '1px solid rgba(255,255,255,0.05)',
                        opacity: cardVisible ? 1 : 0,
                        transform: cardVisible
                          ? 'translateY(0)'
                          : 'translateY(10px)',
                        transition:
                          'opacity 700ms cubic-bezier(0.22,1,0.36,1) 750ms, transform 700ms cubic-bezier(0.22,1,0.36,1) 750ms',
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div
                              className="w-9 h-9 flex items-center justify-center rounded-full"
                              style={{
                                background:
                                  'linear-gradient(135deg, rgba(217,169,78,0.15) 0%, rgba(217,169,78,0.05) 100%)',
                                border: '1px solid rgba(217,169,78,0.2)',
                              }}
                            >
                              <i className="ri-building-2-line text-amber-400 text-sm"></i>
                            </div>
                            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 flex items-center justify-center bg-amber-500 rounded-full">
                              <i className="ri-check-line text-[8px] text-black"></i>
                            </div>
                          </div>
                          <div>
                            <div className="text-[9px] text-white/35 uppercase tracking-wider font-mono">
                              Verified Owner
                            </div>
                            <div className="text-xs text-white/70 font-medium font-body">
                              {SITE_CONFIG.owner.name}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            document
                              .getElementById('contact')
                              ?.scrollIntoView({ behavior: 'smooth' })
                          }
                          className="group/btn relative flex items-center gap-2 px-5 py-2.5 rounded-full overflow-hidden cursor-pointer"
                          style={{
                            background:
                              'linear-gradient(135deg, rgba(217,169,78,0.12) 0%, rgba(217,169,78,0.06) 100%)',
                            border: '1px solid rgba(217,169,78,0.25)',
                          }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-amber-600/5 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500"></div>
                          <span className="relative text-[10px] tracking-[0.12em] text-amber-400 uppercase font-normal whitespace-nowrap font-mono">
                            Contact
                          </span>
                          <i className="relative ri-arrow-right-up-line text-amber-400 text-sm group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform duration-300"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — Why This Domain + CTA */}
            <div ref={rightRef}>
              {/* Section header */}
              <div
                className="mb-8"
                style={{
                  opacity: rightVisible ? 1 : 0,
                  transform: rightVisible
                    ? 'translateX(0)'
                    : 'translateX(14px)',
                  transition:
                    'opacity 800ms cubic-bezier(0.22,1,0.36,1) 100ms, transform 800ms cubic-bezier(0.22,1,0.36,1) 100ms',
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={`h-px bg-gradient-to-r from-amber-500/50 to-transparent transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      rightVisible ? 'w-8' : 'w-0'
                    }`}
                    style={{ transitionDelay: '250ms' }}
                  ></div>
                  <span className="text-[10px] tracking-[0.25em] text-amber-500/60 uppercase font-normal font-mono">
                    Why This Domain
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-light text-white font-display">
                  Commands{' '}
                  <span
                    style={{
                      background:
                        'linear-gradient(135deg, #f5d78e, #c9952e)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Attention
                  </span>
                </h2>
                <p className="text-xs text-white/30 mt-2 leading-relaxed max-w-md font-body">
                  Four pillars of value that make this domain an
                  exceptional digital asset for any ambitious organization.
                </p>
              </div>

              {/* Value Points — each item staggers individually */}
              <div className="space-y-3">
                {valuePoints.map((item, i) => (
                  <div
                    key={i}
                    className="group relative rounded-xl overflow-hidden cursor-default"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      opacity: rightVisible ? 1 : 0,
                      transform: rightVisible
                        ? 'translateY(0) translateX(0)'
                        : 'translateY(10px) translateX(14px)',
                      transition: `opacity 700ms cubic-bezier(0.22,1,0.36,1) ${250 +
                        i * 100}ms, transform 700ms cubic-bezier(0.22,1,0.36,1) ${250 +
                        i * 100}ms, border-color 600ms, background 600ms, box-shadow 600ms`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        'rgba(217,169,78,0.2)';
                      e.currentTarget.style.background =
                        'linear-gradient(135deg, rgba(217,169,78,0.05) 0%, rgba(255,255,255,0.02) 100%)';
                      e.currentTarget.style.boxShadow =
                        '0 8px 30px rgba(0,0,0,0.2), 0 0 0 1px rgba(217,169,78,0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor =
                        'rgba(255,255,255,0.05)';
                      e.currentTarget.style.background =
                        'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/0 group-hover:via-amber-500/30 to-transparent transition-all duration-600"></div>
                    <div className="relative p-4 flex items-start gap-4">
                      <div className="flex-shrink-0 w-6 pt-0.5">
                        <span className="text-[10px] tracking-wider text-amber-500/25 group-hover:text-amber-500/50 transition-colors duration-500 font-medium font-mono">
                          {item.num}
                        </span>
                      </div>
                      <div className="flex-shrink-0">
                        <div
                          className="w-10 h-10 flex items-center justify-center rounded-lg"
                          style={{
                            background:
                              'linear-gradient(135deg, rgba(217,169,78,0.12) 0%, rgba(217,169,78,0.04) 100%)',
                            border: '1px solid rgba(217,169,78,0.15)',
                            transition: 'all 500ms',
                          }}
                        >
                          <i
                            className={`${item.icon} text-lg text-amber-400/80 group-hover:text-amber-400 transition-colors duration-500`}
                          ></i>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-white/90 mb-1 group-hover:text-white transition-colors duration-500 font-mini-title">
                          {item.title}
                        </h3>
                        <p className="text-[11px] text-white/35 leading-relaxed group-hover:text-white/45 transition-colors duration-500 font-body">
                          {item.desc}
                        </p>
                      </div>
                      <div
                        className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 mt-1"
                        style={{
                          background: 'rgba(217,169,78,0.1)',
                          border: '1px solid rgba(217,169,78,0.2)',
                        }}
                      >
                        <i className="ri-arrow-right-up-line text-[10px] text-amber-400/70"></i>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div
                className="flex flex-col sm:flex-row gap-3 mt-8"
                style={{
                  opacity: rightVisible ? 1 : 0,
                  transform: rightVisible
                    ? 'translateY(0)'
                    : 'translateY(12px)',
                  transition:
                    'opacity 700ms cubic-bezier(0.22,1,0.36,1) 700ms, transform 700ms cubic-bezier(0.22,1,0.36,1) 700ms',
                }}
              >
                <button
                  onClick={() =>
                    document
                      .getElementById('contact')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className="group relative flex-1 px-6 py-4 rounded-xl overflow-hidden cursor-pointer"
                  style={{
                    background:
                      'linear-gradient(135deg, #d4a843 0%, #c9952e 50%, #b8860b 100%)',
                    boxShadow:
                      '0 8px 30px rgba(217,169,78,0.2), inset 0 1px 0 rgba(255,255,255,0.15)',
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute top-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/15 to-transparent skew-x-12"></div>
                  <span className="relative flex items-center justify-center gap-2.5 text-xs tracking-[0.15em] text-black font-medium uppercase whitespace-nowrap font-mono">
                    Make an Offer
                    <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform duration-300"></i>
                  </span>
                </button>
                <button
                  onClick={() =>
                    document
                      .getElementById('value')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className="group flex-1 px-6 py-4 rounded-xl cursor-pointer overflow-hidden relative"
                  style={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.02)',
                    transition: 'all 500ms',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor =
                      'rgba(217,169,78,0.3)';
                    e.currentTarget.style.background =
                      'rgba(217,169,78,0.04)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor =
                      'rgba(255,255,255,0.1)';
                    e.currentTarget.style.background =
                      'rgba(255,255,255,0.02)';
                  }}
                >
                  <span className="flex items-center justify-center gap-2.5 text-xs tracking-[0.15em] text-white/60 group-hover:text-white/90 uppercase whitespace-nowrap transition-colors duration-500 font-mono">
                    <i className="ri-sparkling-line text-amber-400"></i>
                    Explore Value
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* ── STATS BAR ── */}
          <div
            ref={statsRef}
            className={`transition-all duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
              statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-14'
            }`}
          >
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 50%, rgba(217,169,78,0.02) 100%)',
                border: '1px solid rgba(255,255,255,0.06)',
                boxShadow:
                  '0 15px 40px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/3 h-px bg-gradient-to-r from-transparent via-amber-500/15 to-transparent"></div>

              <div className="relative p-7 flex flex-wrap items-center justify-center gap-6 md:gap-0">
                {statsItems.map((stat, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-0 md:flex-1"
                    style={{
                      opacity: statsVisible ? 1 : 0,
                      transform: statsVisible
                        ? 'translateY(0)'
                        : 'translateY(10px)',
                      transition: `opacity 1000ms cubic-bezier(0.22,1,0.36,1) ${300 +
                        i * 140}ms, transform 1000ms cubic-bezier(0.22,1,0.36,1) ${300 +
                        i * 140}ms`,
                    }}
                  >
                    <div className="text-center px-4 md:px-2 flex-1">
                      <div className="flex items-center justify-center gap-2 mb-1.5">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i
                            className={`${stat.icon} text-xs ${
                              stat.isGreen
                                ? 'text-emerald-400/50'
                                : 'text-amber-500/30'
                            }`}
                          ></i>
                        </div>
                        {stat.isGreen && (
                          <div className="relative w-2 h-2">
                            <div className="absolute inset-0 bg-emerald-400 rounded-full"></div>
                            <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-40"></div>
                          </div>
                        )}
                        <span
                          className={`text-xl md:text-2xl font-light font-display ${
                            stat.isGreen ? 'text-emerald-400' : ''
                          }`}
                          style={
                            !stat.isGreen
                              ? {
                                  background:
                                    'linear-gradient(135deg, #f5d78e, #c9952e)',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                }
                              : {}
                          }
                        >
                          {stat.value}
                        </span>
                      </div>
                      <div className="text-[9px] tracking-[0.2em] text-white/30 uppercase font-mono">
                        {stat.label}
                      </div>
                    </div>
                    {i < 4 && (
                      <div className="hidden md:block w-px h-10 bg-gradient-to-b from-transparent via-white/[0.06] to-transparent"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ SCROLL INDICATOR ═══ */}
      <div
        className={`absolute bottom-10 left-1/2 -translate-x-1/2 transition-all duration-1000 delay-[1200ms] ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div
          className="flex flex-col items-center gap-3 cursor-pointer group"
          onClick={() =>
            document
              .getElementById('value')
              ?.scrollIntoView({ behavior: 'smooth' })
          }
        >
          <span className="text-[9px] tracking-[0.3em] text-white/25 uppercase group-hover:text-white/45 transition-colors duration-500">
            Discover More
          </span>
          <div className="relative w-5 h-9 border border-white/15 rounded-full flex items-start justify-center p-1.5 group-hover:border-white/30 transition-colors duration-500">
            <div className="w-0.5 h-2 bg-amber-500/60 rounded-full animate-bounce"></div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none"></div>

      <style>{`
        @keyframes slideUpReveal {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes goldShimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes floatParticle {
          0% { transform: translateY(0) translateX(0); opacity: 0.3; }
          100% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
        }
        @keyframes beamPulse {
          0%, 100% { opacity: 0.02; }
          50% { opacity: 0.06; }
        }
        @keyframes shimmer {
          0% { background-position: 0 0; }
          100% { background-position: 200px 200px; }
        }
      `}</style>
    </section>
  );
}
